import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Building2, Users, ShoppingBag, Sparkles, RotateCcw, ArrowRight,
  ShieldCheck, TrendingUp, ListChecks,
} from "lucide-react";
import { toast } from "sonner";

function StatTile({ label, value, icon: Icon, tone = "green", testId }) {
  const toneCls = {
    green: "bg-[#083F22] text-[#E4D3A0]",
    gold:  "bg-[#C9A24B] text-[#0F1E15]",
    ivory: "bg-[#F1EDDF] text-[#0F1E15]",
  }[tone];
  return (
    <div className="relative rounded-2xl overflow-hidden border border-[#0F5E39]/15" data-testid={testId}>
      <div className={`${toneCls} p-5`}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] opacity-70">{label}</div>
            <div className="font-serif text-4xl font-medium mt-2">{value}</div>
          </div>
          <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center">
            <Icon size={18} strokeWidth={1.6}/>
          </div>
        </div>
      </div>
    </div>
  );
}

function TenantRow({ tenant, onSwitch, onWipe, isCurrent, isProtected }) {
  return (
    <div
      className="grid grid-cols-12 items-center gap-3 px-4 py-4 border-b border-[#E4DFCF] hover:bg-[#F6F1E1]/50"
      data-testid={`tenant-row-${tenant.code}`}
    >
      <div className="col-span-12 md:col-span-3 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-[#083F22] text-[#C9A24B] flex items-center justify-center font-serif text-sm">
          {tenant.code?.slice(0, 3)}
        </div>
        <div>
          <div className="font-medium text-[15px] tracking-tight">{tenant.name}</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#4A5A50]">
            {tenant.code}{isCurrent ? " · viewing" : ""}
          </div>
        </div>
      </div>
      <div className="col-span-4 md:col-span-1 text-sm">
        <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Users</div>
        <div className="font-serif text-lg text-[#0F5E39]">{tenant.user_count}</div>
      </div>
      <div className="col-span-4 md:col-span-1 text-sm">
        <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Orders</div>
        <div className="font-serif text-lg text-[#0F5E39]">{tenant.order_count}</div>
      </div>
      <div className="col-span-4 md:col-span-1 text-sm">
        <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">MTD</div>
        <div className="font-serif text-lg text-[#C9A24B]">{tenant.orders_mtd}</div>
      </div>
      <div className="col-span-4 md:col-span-1 text-sm">
        <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Contacts</div>
        <div className="font-serif text-lg">{tenant.contacts_count}</div>
      </div>
      <div className="col-span-4 md:col-span-1 text-sm">
        <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Products</div>
        <div className="font-serif text-lg">{tenant.products_count}</div>
      </div>
      <div className="col-span-4 md:col-span-1 text-sm">
        <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Open Jobs</div>
        <div className="font-serif text-lg">{tenant.production_jobs_open}</div>
      </div>
      <div className="col-span-12 md:col-span-3 flex items-center gap-2 justify-end flex-wrap">
        <ModuleTogglesDialog tenant={tenant} />
        <RolePermissionsDialog tenant={tenant} />
        <Button
          size="sm"
          variant="outline"
          className="rounded-full border-[#0F5E39] text-[#0F5E39] hover:bg-[#0F5E39]/10"
          onClick={() => onSwitch(tenant)}
          data-testid={`switch-tenant-${tenant.code}`}
        >
          <ArrowRight size={14} className="mr-1"/> Open
        </Button>
        {!isProtected && (
          <Button
            size="sm"
            variant="outline"
            className="rounded-full border-red-300 text-red-700 hover:bg-red-50"
            onClick={() => onWipe(tenant)}
            data-testid={`wipe-tenant-${tenant.code}`}
          >
            Wipe data
          </Button>
        )}
      </div>
    </div>
  );
}


function ModuleTogglesDialog({ tenant }) {
  const [open, setOpen] = React.useState(false);
  const [modules, setModules] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const LABELS = {
    assets: "Factory Assets",
    encharge: "Encharge Email",
    work_costing: "Work Costing",
    design_search: "Design Search",
    attendance: "Attendance",
    procurement: "Procurement",
    visitors: "Visitors",
    field_sales: "Field Sales",
    copilot: "TARU Copilot",
    hr_payroll: "HR & Payroll",
  };
  React.useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get(`/super-admin/tenants/${tenant.id}/modules`);
        setModules(data.modules);
      } catch (e) {
        toast.error("Failed to load modules");
      }
    })();
  }, [open, tenant.id]);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/super-admin/tenants/${tenant.id}/modules`, { modules });
      toast.success("Modules updated");
      setOpen(false);
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };
  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className="rounded-full border-[#C9A24B] text-[#8A6600] hover:bg-[#FFF6E1]"
        onClick={() => setOpen(true)}
        data-testid={`modules-tenant-${tenant.code}`}
      >
        Modules
      </Button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-[#FAF7EF] rounded-3xl w-full max-w-md p-5 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid={`modules-dialog-${tenant.code}`}>
            <div className="font-serif text-xl mb-1">{tenant.name}</div>
            <div className="text-[10px] uppercase tracking-widest text-[#4A5A50] mb-4">Enable / disable modules for this tenant</div>
            {!modules ? <div className="text-sm text-[#4A5A50]">Loading…</div> : (
              <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                {Object.keys(LABELS).map((k) => (
                  <label key={k} className="flex items-center justify-between px-3 py-2 rounded-xl bg-white border border-[#E4DFCF] cursor-pointer hover:border-[#0F5E39]/40">
                    <span className="text-sm">{LABELS[k]}</span>
                    <input
                      type="checkbox"
                      checked={modules[k] !== false}
                      onChange={(e) => setModules({ ...modules, [k]: e.target.checked })}
                      className="h-5 w-5 accent-[#0F5E39]"
                      data-testid={`module-toggle-${tenant.code}-${k}`}
                    />
                  </label>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
              <Button className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" onClick={save} disabled={saving || !modules} data-testid={`modules-save-${tenant.code}`}>
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


/** Full role × module matrix. One row per role, one column per module.
 *  Super Admin toggles who can see what, without touching code.
 */
function RolePermissionsDialog({ tenant }) {
  const [open, setOpen] = React.useState(false);
  const [matrix, setMatrix] = React.useState(null);   // {role: {module: bool}}
  const [saving, setSaving] = React.useState(null);   // role currently being saved
  const [dirty, setDirty] = React.useState({});       // {role: {module: bool}}

  const ROLE_LABELS = {
    admin: "Admin",
    manager: "Manager",
    order_team: "Order Team",
    staff: "Order Team (legacy)",
    sales: "Sales",
    accounts: "Accounts",
    production: "Production",
    cad_designer: "CAD Designer",
    watchman: "Watchman",
    client: "Client",
  };
  const MODULE_LABELS = {
    assets: "Assets",
    encharge: "Encharge",
    work_costing: "Work Cost",
    design_search: "Design Search",
    attendance: "Attendance",
    procurement: "Procurement",
    visitors: "Visitors",
    field_sales: "Field Sales",
    copilot: "Copilot",
    hr_payroll: "HR",
  };

  React.useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get(`/super-admin/tenants/${tenant.id}/role-permissions`);
        setMatrix(data.matrix);
        setDirty({});
      } catch (e) { toast.error("Failed to load role permissions"); }
    })();
  }, [open, tenant.id]);

  const flip = (role, key) => {
    setMatrix((m) => ({ ...m, [role]: { ...m[role], [key]: !m[role][key] } }));
    setDirty((d) => ({ ...d, [role]: { ...(d[role] || {}), [key]: !matrix[role][key] } }));
  };

  const saveRole = async (role) => {
    if (!dirty[role]) return;
    setSaving(role);
    try {
      await api.patch(
        `/super-admin/tenants/${tenant.id}/role-permissions/${role}`,
        { modules: dirty[role] },
      );
      toast.success(`${ROLE_LABELS[role] || role} permissions saved`);
      setDirty((d) => { const c = { ...d }; delete c[role]; return c; });
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(null); }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className="rounded-full border-[#0F5E39] text-[#0F5E39] hover:bg-[#0F5E39]/10"
        onClick={() => setOpen(true)}
        data-testid={`role-perms-tenant-${tenant.code}`}
      >
        Role Permissions
      </Button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div
            className="bg-[#FAF7EF] rounded-3xl w-full max-w-5xl p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid={`role-perms-dialog-${tenant.code}`}
          >
            <div className="font-serif text-xl mb-1">{tenant.name} — Role permissions</div>
            <div className="text-[10px] uppercase tracking-widest text-[#4A5A50] mb-4">
              Un-tick a module for a role to hide it from that role — the tenant-wide toggle still wins.
            </div>
            {!matrix ? <div className="text-sm text-[#4A5A50]">Loading…</div> : (
              <div className="overflow-auto max-h-[65vh]">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-widest text-[#4A5A50]">
                      <th className="p-2 sticky left-0 bg-[#FAF7EF]">Role</th>
                      {Object.keys(MODULE_LABELS).map((k) => (
                        <th key={k} className="p-2 text-center">{MODULE_LABELS[k]}</th>
                      ))}
                      <th className="p-2 text-right">Save</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(ROLE_LABELS).filter((r) => matrix[r]).map((role) => (
                      <tr key={role} className="border-t border-[#E4DFCF]">
                        <td className="p-2 font-medium sticky left-0 bg-[#FAF7EF]">{ROLE_LABELS[role]}</td>
                        {Object.keys(MODULE_LABELS).map((k) => (
                          <td key={k} className="p-2 text-center">
                            <input
                              type="checkbox"
                              checked={!!matrix[role][k]}
                              onChange={() => flip(role, k)}
                              className="h-4 w-4 accent-[#0F5E39]"
                              data-testid={`role-perm-${tenant.code}-${role}-${k}`}
                            />
                          </td>
                        ))}
                        <td className="p-2 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full h-7 text-xs"
                            disabled={!dirty[role] || saving === role}
                            onClick={() => saveRole(role)}
                            data-testid={`role-perm-save-${tenant.code}-${role}`}
                          >{saving === role ? "…" : "Save"}</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function SuperAdminPage() {
  const { user, company, refresh } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [masterStats, setMasterStats] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data: over }, { data: ms }] = await Promise.all([
        api.get("/super-admin/overview"),
        api.get("/super-admin/master-contacts/stats").catch(() => ({ data: null })),
      ]);
      setData(over);
      setMasterStats(ms);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load overview");
    }
  };

  useEffect(() => { load(); }, []);

  const onSwitch = async (t) => {
    setBusy(true);
    try {
      await api.post("/super-admin/switch-tenant", { company_id: t.id });
      await refresh();
      toast.success(`Now viewing ${t.name}`);
      // Go to the dashboard of that tenant (super-admin has admin visibility inside).
      nav("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Switch failed");
    } finally {
      setBusy(false);
    }
  };

  const onResetDemo = async () => {
    if (!window.confirm("Wipe & re-seed the DEMO tenant? This deletes all sample orders, contacts, products, production jobs and re-creates them.")) return;
    setBusy(true);
    try {
      await api.post("/super-admin/reset-demo");
      toast.success("DEMO tenant reset & re-seeded.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  const onWipe = async (t) => {
    if (!window.confirm(`Wipe all business data in "${t.name}"? Users, roles, categories and gold rates are preserved.`)) return;
    setBusy(true);
    try {
      await api.post("/super-admin/wipe-tenant-business-data", { company_id: t.id });
      toast.success(`${t.code} business data wiped.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wipe failed");
    } finally {
      setBusy(false);
    }
  };

  const totals = data?.totals || {};

  return (
    <div className="space-y-8" data-testid="super-admin-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A24B]">TARU Platform</div>
          <h1 className="font-serif text-4xl lg:text-5xl font-medium tracking-tight mt-1 text-[#0F1E15]">
            Super Admin Console
          </h1>
          <p className="text-[#4A5A50] mt-1">
            Cross-tenant health, impersonation and sandbox controls.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="rounded-full border-[#C9A24B] text-[#6E5416] hover:bg-[#F6EED8]"
            onClick={onResetDemo}
            disabled={busy}
            data-testid="reset-demo-btn"
          >
            <RotateCcw size={16} className="mr-2"/> Reset DEMO data
          </Button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatTile label="Tenants"     value={totals.companies ?? "—"}    icon={Building2}  tone="green" testId="stat-companies"/>
        <StatTile label="Users"       value={totals.users ?? "—"}         icon={Users}      tone="ivory" testId="stat-users"/>
        <StatTile label="Orders"      value={totals.orders ?? "—"}        icon={ShoppingBag} tone="ivory" testId="stat-orders"/>
        <StatTile label="MTD Orders"  value={totals.orders_mtd ?? "—"}    icon={TrendingUp} tone="gold" testId="stat-mtd-orders"/>
        <StatTile label="Active 7d"   value={totals.active_users_7d ?? "—"} icon={Sparkles}  tone="green" testId="stat-active"/>
      </div>

      {/* Master Contacts panel */}
      <BackupsPanel/>
      <button
        onClick={() => nav("/super-admin/contacts")}
        className="w-full text-left rounded-2xl border border-[#0F5E39]/20 bg-gradient-to-br from-[#083F22] to-[#0F5E39] text-white p-6 hover:shadow-xl transition group"
        data-testid="master-contacts-card"
      >
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A24B]">Platform-only</div>
            <div className="font-serif text-3xl mt-1">Master Contacts</div>
            <p className="text-sm text-[#E4D3A0]/80 mt-1 max-w-lg">
              Cross-tenant lead pool. Never shown in company CRMs until you promote them.
            </p>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-[#C9A24B]/80">Total</div>
              <div className="font-serif text-4xl text-[#C9A24B]">{masterStats?.total ?? "—"}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-[#C9A24B]/80">Customers</div>
              <div className="font-serif text-2xl">{masterStats?.customers ?? "—"}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-[#C9A24B]/80">Leads</div>
              <div className="font-serif text-2xl">{masterStats?.leads ?? "—"}</div>
            </div>
          </div>
        </div>
      </button>

      {/* Tenant table */}
      <Card className="border-[#E4DFCF] rounded-2xl overflow-hidden">
        <CardContent className="p-0">
          <div className="flex items-center gap-2 px-5 pt-5">
            <ListChecks size={16} className="text-[#0F5E39]"/>
            <div className="font-serif text-lg font-medium">Tenants</div>
            <div className="text-xs text-[#4A5A50]">— click Open to impersonate & inspect.</div>
          </div>
          <div className="px-2 mt-3 hidden md:grid grid-cols-12 items-center gap-3 py-2 border-b border-[#E4DFCF] bg-[#F6F1E1]/60 text-[10px] uppercase tracking-widest text-[#4A5A50]">
            <div className="col-span-3 pl-3">Tenant</div>
            <div className="col-span-1">Users</div>
            <div className="col-span-1">Orders</div>
            <div className="col-span-1">MTD</div>
            <div className="col-span-1">Contacts</div>
            <div className="col-span-1">Products</div>
            <div className="col-span-1">Jobs</div>
            <div className="col-span-3 text-right pr-3">Actions</div>
          </div>
          <div>
            {(data?.companies || []).map((t) => (
              <TenantRow
                key={t.id}
                tenant={t}
                onSwitch={onSwitch}
                onWipe={onWipe}
                isCurrent={company?.id === t.id}
                isProtected={t.code === "SUPER" || t.code === "STJ"}
              />
            ))}
            {(!data?.companies || data.companies.length === 0) && (
              <div className="p-10 text-center text-[#4A5A50]">No tenants yet.</div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Info footer */}
      <div className="rounded-2xl border border-[#E4DFCF] bg-[#F6F1E1]/40 p-5 flex items-start gap-3">
        <ShieldCheck size={18} className="text-[#0F5E39] mt-0.5"/>
        <div className="text-sm text-[#4A5A50]">
          <div className="font-medium text-[#0F1E15] mb-1">Guardrails</div>
          <ul className="list-disc pl-4 space-y-1">
            <li><b>STJ</b> — production tenant. No dummy data. Wipe protection is on.</li>
            <li><b>DEMO</b> — sandbox. "Reset DEMO data" restores the full sample dataset.</li>
            <li><b>SUPER</b> — this platform tenant. Cannot be wiped.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}


function BackupsPanel() {
  const [items, setItems] = React.useState([]);
  const [meta, setMeta] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  const load = async () => {
    try {
      const { data } = await api.get("/backup/list");
      setItems(data.items || []);
      setMeta({ retention: data.retention_days, hour: data.schedule_hour_utc });
    } catch (e) { /* noop */ }
  };
  React.useEffect(() => { load(); }, []);

  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/backup/run");
      toast.success(`Snapshot saved (${data.total_docs.toLocaleString()} docs)`);
      load();
    } catch (e) { toast.error("Backup failed"); }
    finally { setBusy(false); }
  };

  const download = async (id) => {
    try {
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const token = localStorage.getItem("token");
      const res = await fetch(`${backend}/api/backup/download/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = id; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("Download failed"); }
  };

  const del = async (id) => {
    if (!window.confirm(`Delete snapshot ${id}? This cannot be undone.`)) return;
    try { await api.delete(`/backup/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Delete failed"); }
  };

  const fmt = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="rounded-2xl border border-[#E4DFCF] bg-white p-5" data-testid="backups-panel">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-[#083F22] text-white flex items-center justify-center">💾</div>
        <div className="flex-1">
          <div className="font-serif text-xl">Backups</div>
          <div className="text-xs text-[#4A5A50]">
            Automatic daily snapshots at {String(meta.hour ?? 3).padStart(2, "0")}:00 UTC · keep last {meta.retention ?? 7}
          </div>
        </div>
        <RestoreDropZone onDone={load}/>
        <Button
          className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white"
          disabled={busy}
          onClick={runNow}
          data-testid="backup-run-btn"
        >
          {busy ? "Working…" : "Snapshot now"}
        </Button>
      </div>

      <div className="mt-4 space-y-2">
        {items.length === 0 && <p className="text-sm text-[#4A5A50] italic">No snapshots yet.</p>}
        {items.map((b) => (
          <div key={b.id} className="flex items-center gap-3 rounded-xl border border-[#E4DFCF] bg-[#F6F1E1]/40 px-3 py-2" data-testid={`backup-row-${b.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-mono text-xs truncate">{b.id}</div>
              <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">{new Date(b.created_at).toLocaleString("en-IN")} · {fmt(b.size_bytes)}</div>
            </div>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => download(b.id)} data-testid={`backup-download-${b.id}`}>Download</Button>
            <Button size="sm" variant="outline" className="rounded-full border-red-200 text-red-700 hover:bg-red-50" onClick={() => del(b.id)} data-testid={`backup-delete-${b.id}`}>Delete</Button>
          </div>
        ))}
      </div>
    </div>
  );
}



function RestoreDropZone({ onDone }) {
  const [confirming, setConfirming] = React.useState(null);   // File awaiting confirm
  const [busy, setBusy] = React.useState(false);
  const [dragOver, setDragOver] = React.useState(false);
  const fileRef = React.useRef(null);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) setConfirming(f);
  };

  const submit = async () => {
    if (!confirming) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", confirming);
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const token = localStorage.getItem("token");
      const res = await fetch(`${backend}/api/backup/upload-and-restore`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      toast.success(`Restored ${Object.keys(data.restored || {}).length} collections`);
      setConfirming(null);
      onDone?.();
    } catch (e) {
      toast.error("Restore failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <input ref={fileRef} type="file" accept=".gz,application/gzip" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) setConfirming(f); if (e.target) e.target.value = ""; }}
        data-testid="restore-file-input"
      />
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        className={`cursor-pointer rounded-2xl border-2 border-dashed p-4 text-center transition ${
          dragOver ? "border-red-500 bg-red-50" : "border-red-300 bg-white hover:bg-red-50/60"
        }`}
        data-testid="restore-dropzone"
      >
        <div className="text-sm font-medium text-red-700">Drop snapshot .json.gz here</div>
        <div className="text-xs text-[#4A5A50] mt-1">or click to browse — wipes current data on confirm</div>
      </div>
      <Button
        size="sm"
        variant="outline"
        className="rounded-full border-red-300 text-red-700 hover:bg-red-50 mt-2 hidden"
        onClick={() => fileRef.current?.click()}
        data-testid="restore-upload-btn"
      >
        Restore from file
      </Button>

      {confirming && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !busy && setConfirming(null)}>
          <div className="bg-white rounded-3xl w-full max-w-md p-5 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="restore-confirm-dialog">
            <div className="font-serif text-xl text-red-700 mb-1">Restore this snapshot?</div>
            <div className="text-sm text-[#4A5A50] mt-1">
              This will <b>wipe all current data</b> and replace it with the contents of:
              <div className="font-mono text-xs mt-2 bg-[#F6F1E1] rounded p-2 break-all">{confirming.name} · {(confirming.size / 1024 / 1024).toFixed(1)} MB</div>
              This cannot be undone.
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" className="rounded-full" disabled={busy} onClick={() => setConfirming(null)}>Cancel</Button>
              <Button className="rounded-full bg-red-600 hover:bg-red-700 text-white" disabled={busy} onClick={submit} data-testid="restore-confirm-btn">
                {busy ? "Restoring…" : "Yes, restore"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

